import {
  Button,
  ButtonsGroup,
  Flex,
  FormItemsGroup,
  Input,
  Link,
  Modal,
  ModalProps,
  Space,
  Spacing,
  Switch,
  Text,
  Textarea,
} from '@billy/design-system'
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react'
import { DefaultValues } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import { DEFAULT_CURRENCY } from '@features/invoices/constants/defaultCurrency'
import { useZervantSettings } from '@features/invoicingSettings/hooks/useZervantSettings'
import { ProductAccountSelect } from '@features/products/elements/ProductAccountSelect'
import { ProductTypeToggleGroup } from '@features/products/elements/ProductTypeToggleGroup'
import { ProductUnitSelect } from '@features/products/elements/ProductUnitSelect'
import { ProductAction } from '@features/products/enums/productAction'
import { Product } from '@features/products/types/product'
import { VatRateSelect } from '@features/vatRates/elements/VatRateSelect'
import { useFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'
import { useGetVatRate } from '@features/vatRates/hooks/useGetVatRate'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { EmberRoute } from '../../../../../enums/emberRoute'
import { useFormContext, useSegment } from '../../../../../hooks'
import { useEmberRouteUrl } from '../../../../../hooks/routing/useEmberRouteUrl'
import { INTERNAL_NOTE_MAX_LENGTH } from '../../../constants/internalNoteMaxLength'
import { PRODUCT_NAME_MAX_LENGTH } from '../../../constants/productNameMaxLength'
import { useProductEditorForm } from '../../../contexts/productEditorFormContext'
import { usePriceCalculations } from '../../../hooks/usePriceCalculations'
import { ProductEditorFormData } from '../../../types/productEditorFormData'
import { ProductEditorFormSchema } from '../../../types/productEditorFormSchema'
import { getOptionalNumberValue } from '../../../utils/getOptionalNumberValue'
import { ProductEditorModalView } from '../types/productEditorModalView'

export interface ProductEditorModalProps extends ModalProps {
  action: ProductAction
  onClose: () => void
  onCreateProductSuccess?: (product: Product) => void
  product: Product
  view: ProductEditorModalView
  withCreateMoreSwitch?: boolean
}

export const ProductEditorModal = ({
  action,
  onClose,
  onCreateProductSuccess,
  product,
  view,
  withCreateMoreSwitch = false,
  ...rest
}: ProductEditorModalProps): ReactElement => {
  const { t } = useTranslation()
  const { track } = useSegment()
  const [isDynamicDefaultsSet, setIsDynamicDefaultsSet] = useState(false)
  const [shouldCreateMore, setShouldCreateMore] = useState(false)
  const { url: OrganizationSettingsUrl } = useEmberRouteUrl(EmberRoute.SettingsOrganization)

  const { submitForm, isSubmitting } = useProductEditorForm()

  const {
    FormItem,
    formState: { defaultValues },
    reset,
    setFocus,
    watch,
  } = useFormContext<ProductEditorFormSchema>()

  const { organization } = useUserOrganization()
  const { handleVatRateChange, handleTotalChange, handlePriceChange } = usePriceCalculations()
  const { isLoading: isLoadingZervantSettings, data } = useZervantSettings()
  const { isLoading: isLoadingFilteredVatRates, getFilteredVatRates } = useFilteredVatRates()
  const { getVatRate } = useGetVatRate()

  const shouldShowVatRelatedFields = !organization?.isVatExempted
  const isFrenchOrganization = organization?.countryId === 'FR'
  const isLoading = isLoadingZervantSettings || isLoadingFilteredVatRates
  const currency = organization?.baseCurrencyId ?? DEFAULT_CURRENCY
  const shouldSetDynamicDefaults = !isLoading && !isDynamicDefaultsSet && action === ProductAction.Create
  const title = [ProductAction.Create, ProductAction.Duplicate].includes(action)
    ? t('products.creation_modal.new_product')
    : product?.name
  const submitButtonText = [ProductAction.Create, ProductAction.Duplicate].includes(action)
    ? t('products.creation_modal.create_product')
    : t('products.creation_modal.update_product')

  const [productName, productType, description] = watch(['productName', 'productType', 'description'])

  const vatRates = useMemo(
    () => (isLoadingFilteredVatRates ? [] : getFilteredVatRates({ productType })),
    [getFilteredVatRates, isLoadingFilteredVatRates, productType],
  )

  const dynamicDefaultValues: DefaultValues<ProductEditorFormData> = useMemo(() => {
    const vatRate = !isLoading
      ? getVatRate({
          vatRateId: data?.values.company.vatRateId,
          vatRates,
        })
      : undefined

    return {
      ...defaultValues,
      unit: data?.managedFields?.UNITS?.[0],
      vatRate,
    }
  }, [data?.managedFields?.UNITS, data?.values.company.vatRateId, defaultValues, getVatRate, isLoading, vatRates])

  const handleCloseButtonClick = useCallback(() => {
    onClose()
  }, [onClose])

  const handleSubmitButtonClick = useCallback(() => {
    track(
      view === 'invoiceEditor'
        ? 'XXX - Invoicing Core - Invoice Editor - Form - New item modal - Create item clicked'
        : 'XXX - Invoicing Core - Item lists - Create item modal - Create item clicked',
    )

    submitForm({
      action,
      shouldCreateMore,
      onSuccess: (product) => {
        if (action === ProductAction.Create) {
          onCreateProductSuccess?.(product)
        }
        if (shouldCreateMore) {
          setFocus('productName')
          reset(dynamicDefaultValues)
        }
      },
    })
  }, [action, dynamicDefaultValues, onCreateProductSuccess, reset, setFocus, shouldCreateMore, submitForm, track, view])

  const handleCreateMoreChange = useCallback(() => {
    setShouldCreateMore((shouldCreateMore) => !shouldCreateMore)
  }, [])

  useEffect(() => {
    if (shouldSetDynamicDefaults) {
      reset(dynamicDefaultValues)
      setIsDynamicDefaultsSet(true)
    }
  }, [dynamicDefaultValues, reset, shouldSetDynamicDefaults])

  return (
    <Modal size="s" {...rest}>
      <Modal.Header title={title} />

      <Modal.Body>
        <FormItemsGroup
          itemsInRow={3}
          itemsTemplate={['min-content', 'auto', 'auto']}
          rowSpacing={Spacing.S24}
          spacing={Spacing.S16}
        >
          <FormItem
            itemColumnEnd={4}
            itemColumnStart={1}
            label={
              <Flex alignItems="center" justifyContent="space-between">
                <Text inherit>{t('products.creation_modal.product_name')}</Text>
                <Text colorVariant="secondary" variant="bodySM">
                  {productName?.length ?? 0} / {PRODUCT_NAME_MAX_LENGTH}
                </Text>
              </Flex>
            }
            name="productName"
            render={({ field }) => (
              <Input
                {...field}
                autoFocus
                maxLength={PRODUCT_NAME_MAX_LENGTH}
                placeholder={t('products.creation_modal.product_name_placeholder')}
              />
            )}
          />

          <FormItem
            itemColumnEnd={2}
            itemColumnStart={1}
            label={t('products.creation_modal.product_type')}
            name="productType"
            render={({ field }) => (
              <ProductTypeToggleGroup defaultValue={defaultValues?.productType} onChange={field.onChange} withLabels />
            )}
          />

          <FormItem
            itemColumnEnd={4}
            itemColumnStart={2}
            label={t('products.creation_modal.unit_type')}
            name="unit"
            render={({ field: { onChange, ...field } }) => (
              <ProductUnitSelect
                {...field}
                dropdownFetching={isLoadingZervantSettings}
                dropdownSize="fitTrigger"
                onSelect={onChange}
                selectLook={false}
              />
            )}
          />
          {shouldShowVatRelatedFields && (
            <>
              <FormItem
                label={t('products.creation_modal.price_excluding_vat')}
                name="price"
                render={({ field: { onChange, ...field } }) => (
                  <Input
                    {...field}
                    alignment="right"
                    type="number"
                    onChange={({ target: { value } }) => {
                      const numberValue = getOptionalNumberValue(value)
                      onChange(numberValue)
                      handlePriceChange(numberValue)
                    }}
                    suffix={currency}
                  />
                )}
              />

              <FormItem
                label={t('products.creation_modal.vat')}
                name="vatRate"
                render={({ field: { value, onChange, ...rest } }) => (
                  <VatRateSelect
                    {...rest}
                    dropdownFetching={isLoadingFilteredVatRates}
                    dropdownSize="fitTrigger"
                    onSelect={(_id, vatRate) => {
                      onChange(vatRate)
                      handleVatRateChange(vatRate?.rate)
                    }}
                    selectLook={false}
                    selectedId={value?.id}
                    vatRates={vatRates}
                    withSearch={false}
                  />
                )}
              />

              <FormItem
                label={t('products.creation_modal.price_including_vat')}
                name="total"
                render={({ field: { onChange, ...field } }) => (
                  <Input
                    {...field}
                    alignment="right"
                    type="number"
                    onChange={({ target: { value } }) => {
                      const numberValue = getOptionalNumberValue(value)
                      onChange(numberValue)
                      handleTotalChange(numberValue)
                    }}
                    suffix={currency}
                  />
                )}
              />
            </>
          )}
        </FormItemsGroup>

        {!shouldShowVatRelatedFields && (
          <>
            <FormItem
              itemColumnEnd={4}
              itemColumnStart={1}
              label={t('products.creation_modal.price')}
              name="price"
              render={({ field: { onChange, ...field } }) => (
                <Input
                  {...field}
                  alignment="right"
                  type="number"
                  onChange={({ target: { value } }) => {
                    const numberValue = getOptionalNumberValue(value)
                    onChange(numberValue)
                    handlePriceChange(numberValue)
                  }}
                  suffix={currency}
                />
              )}
            />
            <Input name="total" hidden />
            <Text colorVariant="secondary" variant="bodySM">
              <Trans i18nKey="products.creation_modal.vat_exempt_notice">
                Your company is VAT exempt, you can adjust this setting{' '}
                <Text as="span" inherit colorVariant="primary" weight="bold">
                  <Link to={OrganizationSettingsUrl} underlined>
                    here
                  </Link>
                </Text>{' '}
                if necessary.
              </Trans>
            </Text>
            <Space size="s16" />
          </>
        )}

        <FormItemsGroup
          itemsInRow={3}
          itemsTemplate={['min-content', 'auto', 'auto']}
          rowSpacing={Spacing.S24}
          spacing={Spacing.S16}
        >
          {isFrenchOrganization && (
            <FormItem
              itemColumnEnd={4}
              itemColumnStart={1}
              label={t('products.creation_modal.account')}
              name="accountId"
              render={({ field: { value, onChange } }) => (
                <ProductAccountSelect
                  dropdownSize="fitTrigger"
                  onSelect={(id) => onChange(id)}
                  selectLook={false}
                  selectedId={value}
                />
              )}
            />
          )}

          <FormItem
            itemColumnEnd={4}
            itemColumnStart={1}
            label={
              <Flex alignItems="center" justifyContent="space-between">
                <Text inherit>{t('products.creation_modal.internal_note')}</Text>
                <Text colorVariant="secondary" variant="bodySM">
                  {description?.length ?? 0} / {INTERNAL_NOTE_MAX_LENGTH}
                </Text>
              </Flex>
            }
            name="description"
            render={({ field }) => (
              <Textarea
                {...field}
                expandOnType
                maxHeight={160}
                maxLength={INTERNAL_NOTE_MAX_LENGTH}
                placeholder={t('products.creation_modal.internal_note_placeholder')}
                resizable={false}
                rows={1}
              />
            )}
          />
        </FormItemsGroup>
      </Modal.Body>

      <Modal.Footer>
        <Flex justifyContent={withCreateMoreSwitch ? 'space-between' : 'flex-end'} alignItems="center" flexGrow={1}>
          {withCreateMoreSwitch && (
            <Switch onChange={handleCreateMoreChange} checked={shouldCreateMore}>
              {t('products.creation_modal.create_more')}
            </Switch>
          )}
          <ButtonsGroup>
            <Button variant="text" onClick={handleCloseButtonClick}>
              {t('products.creation_modal.cancel')}
            </Button>
            <Button disabled={isLoading} loading={isSubmitting} onClick={handleSubmitButtonClick}>
              {submitButtonText}
            </Button>
          </ButtonsGroup>
        </Flex>
      </Modal.Footer>
    </Modal>
  )
}
